import { useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';
import type { ClientMessage, ServerMessage } from '../../../shared/types';

/** Same-origin WS URL: https://host/tic-tac-toe → wss://host/tic-tac-toe/ws.
 *  In production Caddy strips the base path, so the server sees /ws. */
function resolveWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const base = import.meta.env.BASE_URL || '/';
  const path = base.replace(/\/$/, '') + '/ws';
  return `${proto}://${window.location.host}${path}`;
}

const RETRY_BASE_MS = 800;
const RETRY_MAX_MS = 8000;

export interface GameSocketApi {
  createRoom: (playerName: string) => void;
  joinRoom: (roomCode: string, playerName: string) => void;
  setReady: (ready: boolean) => void;
  startGame: () => void;
  makeMove: (index: number) => void;
  playAgain: () => void;
  sendChat: (text: string) => void;
  leave: () => void;
  addBot: (difficulty: 'easy' | 'medium' | 'hard') => void;
  removeBot: (playerId: string) => void;
  setBotDifficulty: (playerId: string, difficulty: 'easy' | 'medium' | 'hard') => void;
}

export function useGameSocket(): GameSocketApi {
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(1);
  const closedByUser = useRef(false);
  const store = useGameStore;

  const connect = useCallback(() => {
    if (closedByUser.current) return;
    const ws = new WebSocket(resolveWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 1;
      store.getState().setConnected(true);
      store.getState().setError(null);
      const s = store.getState();
      if (s.roomCode && s.myPlayerId && s.identity) {
        ws.send(JSON.stringify({ type: 'RECONNECT', roomCode: s.roomCode, playerId: s.myPlayerId, identity: s.identity }));
      }
    };

    ws.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }
      handleServerMessage(msg);
    };

    ws.onclose = () => {
      store.getState().setConnected(false);
      if (closedByUser.current) return;
      const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * retryRef.current);
      retryRef.current *= 2;
      window.setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [store]);

  useEffect(() => {
    closedByUser.current = false;
    connect();
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState > 1)) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      closedByUser.current = true;
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  return {
    createRoom: (playerName) => send({ type: 'CREATE_ROOM', playerName }),
    joinRoom: (roomCode, playerName) => send({ type: 'JOIN_ROOM', roomCode, playerName }),
    setReady: (ready) => send({ type: 'SET_READY', ready }),
    startGame: () => send({ type: 'START_GAME' }),
    makeMove: (index) => send({ type: 'MAKE_MOVE', index }),
    playAgain: () => send({ type: 'PLAY_AGAIN' }),
    sendChat: (text) => send({ type: 'SEND_CHAT', text }),
    addBot: (difficulty) => send({ type: 'ADD_BOT', difficulty }),
    removeBot: (playerId) => send({ type: 'REMOVE_BOT', playerId }),
    setBotDifficulty: (playerId, difficulty) => send({ type: 'SET_BOT_DIFFICULTY', playerId, difficulty }),
    leave: () => {
      closedByUser.current = false;
      const s = store.getState();
      s.reset();
      if (wsRef.current) wsRef.current.close();
      window.setTimeout(() => {
        closedByUser.current = false;
        connect();
      }, 400);
    },
  };
}

function handleServerMessage(msg: ServerMessage): void {
  const s = useGameStore.getState();
  switch (msg.type) {
    case 'ERROR':
      s.setError(msg.message);
      window.setTimeout(() => s.setError(null), 4000);
      // Fatal seat errors → drop back to the lobby instead of hanging.
      if (['ROOM_NOT_FOUND', 'ROOM_FULL', 'AUTH_FAILED', 'SERVER_FULL'].includes(msg.code)) {
        s.reset();
      }
      break;
    case 'ROOM_CREATED':
    case 'ROOM_JOINED':
      s.setConnectionInfo({ roomCode: msg.roomCode, playerId: msg.playerId, identity: msg.identity });
      break;
    case 'RECONNECTED':
      s.setError(null);
      break;
    case 'ROOM_SNAPSHOT':
      s.setSnapshot(msg.snapshot);
      break;
    case 'PRIVATE_STATE':
      s.setPrivateState(msg.state);
      break;
    case 'GAME_STARTED':
    case 'GAME_ENDED':
      s.setSnapshot(msg.snapshot);
      if ('state' in msg && msg.state) s.setPrivateState(msg.state);
      break;
    case 'CHAT':
    case 'PLAYER_JOINED':
    case 'PLAYER_LEFT':
      // Full snapshot follows via ROOM_SNAPSHOT.
      break;
  }
}
